import * as Common from "@sp-crm/core";
import {
    DefaultCommunitySearch,
    DefaultCommunitySearchLocationFilter,
    IPlacement,
    isValidDefaultCommunitySearch,
    Localization,
    Meters,
    SettingsCommunitySortType,
} from "@sp-crm/core";
import {
    ISearchAddress,
    ISearchAnswer,
    ISearchPrice,
    ISearchRating,
    isSearchLocationValid,
} from "components/community-search/search-bar/util";
import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { communitySearchInitializeEvent } from "store/actions/communities";
import { usePreferences, useRegionId } from "store/selectors/hooks";
import { currentLocale } from "store/selectors/regions";
import { useTenantSettings } from "store/selectors/settings";
import {
    buildDefaultSearchInstance,
    buildLocationFilterSearchInstance,
    CommunitySearchInstance,
} from "../../../store/reducers/community-search";
import { SearchCommunityState } from "../../community-search/search-bar/search-bar";
import { getFirstTypeFilterQuestion } from "util/question";
import { FindCommunities } from "./find-communities";

export interface INeedsAndDesiresFilters {
    address: ISearchAddress;
    answers: ISearchAnswer[];
    rating: ISearchRating;
    price: ISearchPrice;
}

function isBinarySelected(placement: IPlacement, question: Common.IQuestion): boolean {
    if (placement) {
        const result = placement.answers.find(q => q.questionId == question.id);
        if (result && result.booleanAnswer) {
            return true;
        }
    }
    return false;
}

function selectedOptions(placement: IPlacement, question: Common.IQuestion): string[] {
    if (placement) {
        const result = placement.answers.find(q => q.questionId == question.id);
        if (result) {
            const selections = result.selections.slice(0);
            return selections.filter(s => {
                const prompt = question.prompts.find(p => p.id == s);
                if (prompt && prompt.ignoreMatching) {
                    return false;
                }
                return true;
            });
        }
    }
    return [];
}

function searchAdvancedAnswers(
    placement: IPlacement,
    questions: Common.IQuestion[],
): ISearchAnswer[] {
    const firstTypeQuestion = getFirstTypeFilterQuestion(questions);

    if (!firstTypeQuestion) {
        return [];
    }

    return [firstTypeQuestion]
        .filter(q => {
            return (
                (q.questionType == Common.QuestionType.binary &&
                    isBinarySelected(placement, q)) ||
                q.questionType == Common.QuestionType.nary
            );
        })
        .map(q => {
            if (
                typeof q.matchingCriteria === "undefined" ||
                q.matchingCriteria === null ||
                q.interaction === Common.InteractionType.none ||
                q.searchBehavior === Common.QuestionBehavior.alwaysHide
            ) {
                return null;
            }
            if (
                q.questionType == Common.QuestionType.binary &&
                isBinarySelected(placement, q)
            ) {
                const theBool: ISearchAnswer = {
                    questionId: q.id,
                    booleanAnswer: "true",
                    selectionAnswer: [],
                };
                return theBool;
            }
            if (q.questionType == Common.QuestionType.nary) {
                const theSelectionAnswer: ISearchAnswer = {
                    questionId: q.id,
                    booleanAnswer: "na",
                    selectionAnswer: selectedOptions(placement, q),
                };
                return theSelectionAnswer;
            }
            return null;
        })
        .filter(x => !!x);
}

function bestLocation(placement: IPlacement): ISearchAddress {
    const address = {
        street: placement.bestLocationAddress,
        city: placement.bestLocationCity,
        state: placement.bestLocationState,
        zip: placement.bestLocationZip,
        county: "",
    };

    return isSearchLocationValid(address)
        ? address
        : { zip: "", street: "", state: "", city: "", county: "" };
}

function getNeedsAndDesiresFilters(
    placement: IPlacement,
    questions: Common.IQuestion[],
    defaultCommunitySortOrder: SettingsCommunitySortType,
    defaultCommunitySearch?: DefaultCommunitySearch,
    locale?: Localization,
): SearchCommunityState {
    const location: ISearchAddress = bestLocation(placement);
    const hasIdealLocation = isSearchLocationValid(location);
    const useLocationFilter =
        !hasIdealLocation &&
        defaultCommunitySearch?.type === "locationFilter" &&
        isValidDefaultCommunitySearch(defaultCommunitySearch);
    const locationFilterSettings = useLocationFilter
        ? (defaultCommunitySearch as DefaultCommunitySearchLocationFilter)
        : undefined;
    const effectiveLocation = locationFilterSettings
        ? {
              zip: locationFilterSettings.zip,
              street: locationFilterSettings.address,
              city: locationFilterSettings.city,
              state: locationFilterSettings.state,
              county: "",
          }
        : location;
    const effectiveMaxDistance =
        locationFilterSettings && locale?.conversions
            ? locale.conversions.fromMeters(locationFilterSettings.distance as Meters)
            : 5;
    return {
        address: {
            zip: effectiveLocation.zip,
            street: effectiveLocation.street,
            city: effectiveLocation.city,
            state: effectiveLocation.state,
            county: "",
        },
        answers: searchAdvancedAnswers(placement, questions),
        rating: {
            value: 0,
            includeUnrated: true,
        },
        price: { min: 0, max: 10000, includePriceUndefined: true },
        searchText: "",
        maxDistance: effectiveMaxDistance,
        specificZip: false,
        specificCity: false,
        specificCounty: false,
        specificState: false,
        bothGenders: false,
        maleOnly: false,
        femaleOnly: false,
        genderNotSet: false,
        contractNoState: false,
        contractPending: false,
        contractActive: false,
        contractOneOff: false,
        contractNone: false,
        contractTerminated: false,
        licensePending: false,
        licenseActive: false,
        licenseOnProbation: false,
        licenseClosed: false,
        licenseNotApplicable: false,
        licenseNotSet: false,
        minCapacity: null,
        maxCapacity: null,
        sort: defaultCommunitySortOrder,
        hideDontShow: true,
        matchHighScore: "",
        matchLowScore: "",
        matchRule: "",
    };
}

function buildSearchesDiff(
    placement: IPlacement,
    questions: Common.IQuestion[],
    defaultCommunitySortOrder: SettingsCommunitySortType,
    defaultCommunitySearch?: DefaultCommunitySearch,
    locale?: Localization,
): Partial<CommunitySearchInstance> {
    const defaultFilters = getNeedsAndDesiresFilters(
        placement,
        questions,
        defaultCommunitySortOrder,
        defaultCommunitySearch,
        locale,
    );
    return {
        zip: defaultFilters.address.zip,
        address: defaultFilters.address.street,
        city: defaultFilters.address.city,
        state: defaultFilters.address.state,
        advancedAnswers: defaultFilters.answers,
        rating: defaultFilters.rating,
        price: defaultFilters.price,
        hideDontShow: defaultFilters.hideDontShow,
        sort: defaultFilters.sort,
        maxDistance: defaultFilters.maxDistance as Common.LocalizedDistance,
    };
}

interface ClientCommunityProps {
    placement?: IPlacement;
    questions: Common.IQuestion[];
}

const ClientCommunity: React.FC<ClientCommunityProps> = ({ placement, questions }) => {
    const regionId = useRegionId("community");
    const { defaultCommunitySortOrder } = usePreferences();
    const dispatch = useDispatch();
    const {
        settings: { defaultCommunitySearch },
    } = useTenantSettings();
    const locale = useSelector(currentLocale);

    const searchKey = `client-${placement.id}`;

    useEffect(() => {
        if (!regionId) {
            return;
        }
        const initSearchInstance = {
            ...buildDefaultSearchInstance(regionId, defaultCommunitySortOrder),
            ...buildSearchesDiff(
                placement,
                questions,
                defaultCommunitySortOrder,
                defaultCommunitySearch,
                locale,
            ),
        };
        dispatch(
            communitySearchInitializeEvent(
                searchKey,
                defaultCommunitySortOrder,
                regionId,
                initSearchInstance,
            ),
        );
    }, [regionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const defaultSearchInstance = buildLocationFilterSearchInstance(
        defaultCommunitySearch,
        regionId,
        defaultCommunitySortOrder,
        locale,
    );

    return (
        <div className="mb-2 bg-zinc-50 min-h-screen border-t border-t-gray-200">
            <FindCommunities
                placement={placement}
                searchKey={searchKey}
                needsAndDesiresFilters={getNeedsAndDesiresFilters(
                    placement,
                    questions,
                    defaultCommunitySortOrder,
                    defaultCommunitySearch,
                    locale,
                )}
                defaultSearchInstance={defaultSearchInstance}
            />
        </div>
    );
};
export { ClientCommunity };
